import type { B18User, PermissionRule } from '@/services/api'

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const isPermissionRule = (value: unknown): value is PermissionRule => {
  if (!isRecord(value) || typeof value.subject !== 'string') return false
  const { action } = value
  if (typeof action === 'string') return true
  return (
    Array.isArray(action) &&
    action.length > 0 &&
    action.every(item => typeof item === 'string')
  )
}

/**
 * The rules, or `null` when the old matrix must answer instead: not sent, or any rule malformed.
 * An empty array is NOT "not sent" — it means the role grants nothing (agreed with the backend
 * 2026-09-24), so a save that revokes everything sticks. All-or-nothing, as in the web app —
 * dropping one bad rule would silently lose a permission.
 */
const readRules = (value: unknown): PermissionRule[] | null => {
  if (!Array.isArray(value)) return null
  return value.every(isPermissionRule) ? value : null
}

/**
 * Does the user hold `<subject>.<action>`?
 *
 * Prefers `role.permissions_rules` — the named permissions the backend sends on login — and falls
 * back to the old `role.permissions` true/false matrix for a session from before the backend change.
 * The last matching rule wins, as in CASL, so a later `inverted` rule is a deny.
 *
 * ⚠️ The fallback reads `permissions[subject][action]`, which is the same permission only when the
 * key was not renamed. `load.read` was not; 40 of the backend's 87 v1 keys were (see `V1_KEYS` in the
 * Backend repo). Check that list before using this for another key.
 */
export const hasPermission = (
  user: B18User | null | undefined,
  subject: string,
  action: string
): boolean => {
  const rules = readRules(user?.role?.permissions_rules)
  if (rules) {
    let allowed = false
    for (const rule of rules) {
      const actions = ([] as string[]).concat(rule.action)
      const matches =
        (rule.subject === subject || rule.subject === 'all') &&
        (actions.includes(action) || actions.includes('manage'))
      if (matches) allowed = !rule.inverted
    }
    return allowed
  }

  const group = user?.role?.permissions?.[subject]
  return isRecord(group) && group[action] === true
}
